import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Wizard } from 'components/layout/wizards';
import {
  WORKER_TYPES,
  WORKER_PARSER_VALIDATION_TYPES,
  WORKER_PATH_OPERATIONS
} from 'constants/workers';
import { createWorker } from 'store/slices/workersSlice';
import WorkerTypeStep from './steps/WorkerTypeStep/WorkerTypeStep';
import ParserUrlStep from './steps/ParserUrlStep/ParserUrlStep';
import PrebuildWorkerStep from './steps/PrebuildWorkerStep/PrebuildWorkerStep';
import ParserValidationStep from './steps/ParserValidationStep/ParserValidationStep';
import ValidationLogicStep from './steps/ValidationLogicStep/ValidationLogicStep';
import WorkerReviewStep from './steps/WorkerReviewStep/WorkerReviewStep';
import AttachRemindersStep from './steps/AttachRemindersStep/AttachRemindersStep';

const ADD_WORKER_STEPS = {
  TYPE: { id: 'type', component: WorkerTypeStep },
  PARSER_URL: { id: 'parser-url', component: ParserUrlStep },
  PREBUILD_SELECT: { id: 'prebuild-select', component: PrebuildWorkerStep },
  PARSER_VALIDATION: { id: 'parser-validation', component: ParserValidationStep },
  VALIDATION_LOGIC: { id: 'validation-logic', component: ValidationLogicStep },
  REVIEW: { id: 'review', component: WorkerReviewStep },
  ATTACH: { id: 'attach', component: AttachRemindersStep },
};

const getSteps = (data) => {
  const steps = [ADD_WORKER_STEPS.TYPE];

  if (data.type === WORKER_TYPES.PARSER) {
    steps.push(ADD_WORKER_STEPS.PARSER_URL);
    steps.push(ADD_WORKER_STEPS.PARSER_VALIDATION);
    steps.push(ADD_WORKER_STEPS.VALIDATION_LOGIC);
    steps.push(ADD_WORKER_STEPS.REVIEW);
  } else if (data.type === WORKER_TYPES.PREBUILD) {
    steps.push(ADD_WORKER_STEPS.PREBUILD_SELECT);
    steps.push(ADD_WORKER_STEPS.REVIEW);
  } else {
    // Default placeholders if type not selected yet
    steps.push(ADD_WORKER_STEPS.PARSER_URL); // Just to show progress dots
    steps.push(ADD_WORKER_STEPS.REVIEW);
  }

  // Always show attach step
  steps.push(ADD_WORKER_STEPS.ATTACH);

  return steps;
};

export default function AddWorkerWizard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [wizardData, setWizardData] = useState({});

  const steps = useMemo(() => getSteps(wizardData), [wizardData]);

  const currentStepId = searchParams.get('step') || 'type';
  const currentStepIndex = useMemo(() => {
    const index = steps.findIndex(s => s.id === currentStepId);
    return index >= 0 ? index : 0;
  }, [steps, currentStepId]);

  // Sync URL and validate step access
  useEffect(() => {
    // If we are at the start and URL says something else (and not in attach mode), reset to type
    if (currentStepIndex === 0 && currentStepId !== 'type' && !wizardData.createdWorkerId) {
      setSearchParams({ step: 'type' }, { replace: true });
      return;
    }

    // Prevent jumping ahead without required data
    if (currentStepIndex > 0 && !wizardData.createdWorkerId) {
      // Must have a type selected to go beyond step 0
      if (!wizardData.type) {
        setSearchParams({ step: 'type' }, { replace: true });
        return;
      }

      // Specific checks based on type
      if (wizardData.type === WORKER_TYPES.PARSER) {
        // Steps: Type(0) -> Url(1) -> Validation(2) -> Logic(3) -> Review(4) -> Attach(5)
        // If at validation (index 2) or later, need URL/Method from step 1
        if (currentStepIndex > 1 && (!wizardData.url || !wizardData.method)) {
           setSearchParams({ step: 'parser-url' }, { replace: true });
           return;
        }

        // If at logic (index 3) or later, need validation expression from step 2
        if (currentStepIndex > 2 && (!wizardData.validationType || !wizardData.expression)) {
           setSearchParams({ step: 'parser-validation' }, { replace: true });
           return;
        }
      } else if (wizardData.type === WORKER_TYPES.PREBUILD) {
         // Steps: Type(0) -> Select(1) -> Review(2) -> Attach(3)

         // If at review (index 2), need selected worker from step 1
         if (currentStepIndex > 1 && !wizardData.selectedWorker) {
            setSearchParams({ step: 'prebuild-select' }, { replace: true });
            return;
         }
      }

      // Prevent manual access to attach step if not created
      const attachIndex = steps.findIndex(s => s.id === 'attach');
      if (currentStepIndex === attachIndex) {
         // Determine previous step (Review)
         const reviewStep = steps.find(s => s.id === 'review');
         if (reviewStep) {
            setSearchParams({ step: 'review' }, { replace: true });
            return;
         }
      }
    }
  }, [currentStepIndex, currentStepId, setSearchParams, wizardData, steps]);

  const handleClose = () => {
    navigate('/workers');
  };

  const handleCreateWorker = async (data) => {
    try {
      const payload = {
        name: data.name,
        type: data.type,
      };

      if (data.type === WORKER_TYPES.PARSER) {
        payload.config = {
          url: data.url,
          method: data.method,
          headers: data.headers || {},
          body: data.body || null,
          onMatch: {
            action: data.onSuccess,
            messageTemplate: data.onSuccessMessage
          },
          onNoMatch: {
            action: data.onFailure,
            messageTemplate: data.onFailureMessage
          }
        };

        if (data.validationType === WORKER_PARSER_VALIDATION_TYPES.REGEX) {
          payload.config.pattern = data.expression;
        } else if (data.validationType === WORKER_PARSER_VALIDATION_TYPES.JSON) {
          payload.config.jsonPath = data.expression;
        }

        if (data.operation && data.operation !== WORKER_PATH_OPERATIONS.NONE) {
          payload.config.operation = data.operation;
          payload.config.controlValue = data.controlValue;
        }
      } else if (data.type === WORKER_TYPES.PREBUILD) {
        // For prebuild workers, we might need to pass the selected worker ID or config
        // Assuming we copy the config from the selected prebuild worker
        payload.config = {
          ...data.selectedWorker?.config
        };
      }

      const newWorker = await dispatch(createWorker(payload)).unwrap();
      // Update wizard data to include created ID, which triggers the Attach step view
      setWizardData({ ...data, createdWorkerId: newWorker._id });
      setSearchParams({ step: 'attach' });
    } catch (error) {
      console.error('Failed to create worker:', error);
    }
  };

  const handleStepChange = (nextIndex, newData) => {
    // If we're done (finished last step), close
    const nextSteps = getSteps(newData);
    if (nextIndex >= nextSteps.length) {
      navigate('/workers');
      return;
    }

    // Check if we are transitioning to Attach step but haven't created worker yet
    const targetStep = nextSteps[nextIndex];
    if (targetStep.id === 'attach' && !newData.createdWorkerId) {
      handleCreateWorker(newData);
      return;
    }

    setSearchParams({ step: targetStep.id });
  };

  return (
    <Wizard
      steps={steps}
      currentStepIndex={currentStepIndex}
      onClose={handleClose}
      onStepChange={handleStepChange}
      onDataChange={setWizardData}
      onComplete={() => navigate('/workers')}
      initialData={wizardData}
      title="Add Worker"
    />
  );
}
